import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Video, FileText, ClipboardList, LayoutGrid,
  HelpCircle, Award, Gift, Target, FolderOpen, Briefcase,
  HeadphonesIcon, BarChart3, TrendingUp, Menu, ChevronRight,
  ArrowLeft, FolderGit2
} from 'lucide-react';
import CourseHome from '../components/course-dashboard/CourseHome';
import StudentIDCard from '../components/course-dashboard/StudentIDCard';
import AccountDetails from '../components/course-dashboard/AccountDetails';
import UpcomingClasses from '../components/course-dashboard/UpcomingClasses';
import ClassRecordings from '../components/course-dashboard/ClassRecordings';
import NotesSection from '../components/course-dashboard/NotesSection';
import AssignmentsSection from '../components/course-dashboard/AssignmentsSection';
import QuizzesSection from '../components/course-dashboard/QuizzesSection';
import BadgesSection from '../components/course-dashboard/BadgesSection';
import CourseRewards from '../components/course-dashboard/CourseRewards';
import MockTests from '../components/course-dashboard/MockTests';
import ResourcesSection from '../components/course-dashboard/ResourcesSection';
import PlacementSection from '../components/course-dashboard/PlacementSection';
import SupportSection from '../components/course-dashboard/SupportSection';
import LeaderboardSection from '../components/course-dashboard/LeaderboardSection';
import PerformanceSection from '../components/course-dashboard/PerformanceSection';
import ProjectSubmission from '../components/course-dashboard/ProjectSubmission';
import axios from 'axios';
import { UserContext } from '@/lib/User';
import { trackScreen, flushCurrentScreen } from '@/lib/activityTracker';

const navItems = [
  { id: 'home',          label: 'Overview',          icon: LayoutGrid },
  { id: 'upcoming',      label: 'Classes',           icon: Calendar },
  { id: 'recordings',    label: 'Recordings',        icon: Video },
  { id: 'notes',         label: 'Notes',             icon: FileText },
  { id: 'assignments',   label: 'Assignments',       icon: ClipboardList },
  { id: 'projects',      label: 'Projects',          icon: FolderGit2 },
  // { id: 'quizzes',       label: 'Quizzes',           icon: HelpCircle },
  // { id: 'badges',        label: 'Badges & Rewards',  icon: Award },
  // { id: 'course-rewards',label: 'Course Rewards',    icon: Gift },
  // { id: 'mock-tests',    label: 'Mock Tests',        icon: Target },
  // { id: 'resources',     label: 'Resources',         icon: FolderOpen },
  { id: 'placement',     label: 'Placement',         icon: Briefcase },
  { id: 'support',       label: 'Support',           icon: HeadphonesIcon },
  // { id: 'leaderboard',   label: 'Leaderboard',       icon: BarChart3 },
  // { id: 'performance',   label: 'Performance',       icon: TrendingUp },
];

const sectionComponents = {
  'home':          CourseHome,
  'id-card':       StudentIDCard,
  'account':       AccountDetails,
  'upcoming':      UpcomingClasses,
  'recordings':    ClassRecordings,
  'notes':         NotesSection,
  'assignments':   AssignmentsSection,
  'projects':      ProjectSubmission,
  'quizzes':       QuizzesSection,
  'badges':        BadgesSection,
  'course-rewards':CourseRewards,
  'mock-tests':    MockTests,
  'resources':     ResourcesSection,
  'placement':     PlacementSection,
  'support':       SupportSection,
  'leaderboard':   LeaderboardSection,
  'performance':   PerformanceSection,
};

export default function CourseDashboard() {
  const { course_url } = useParams();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    const sec = searchParams.get('section');
    return sec && sectionComponents[sec] ? sec : 'home';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useContext(UserContext);

  const [course, setCourse] = React.useState(null);

  // Use email as cache key — stable across logout/login cycles unlike session token
  const photoCacheKey = `xceedEdge_photo_${user?.email || user?.id || 'u'}`;

  const getCourse = async () => {
    try {
      const response = await axios.post(
        'https://xceededge.com/get-course-dashboard.php',
        { course_url, session: user.session },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success === true) {
        const data = response.data.course;
        // If server doesn't return profile_photo yet (old PHP deployed),
        // fall back to the locally cached URL from the last successful upload
        if (!data.profile_photo) {
          const cached = localStorage.getItem(photoCacheKey);
          if (cached) data.profile_photo = cached;
        } else {
          // Keep cache in sync with whatever the server returns
          localStorage.setItem(photoCacheKey, data.profile_photo);
        }
        setCourse(data);
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
    }
  };
    useEffect(() => {
      getCourse();
    }, [course_url]);

  // Track time spent on each course-dashboard section (Notes, Recordings, etc.)
  useEffect(() => {
    if (!user?.session || !course) return;

    const sectionLabel = navItems.find(n => n.id === activeSection)?.label || activeSection;
    const path = `/dashboard/${course_url}?section=${activeSection}`;
    const label = `${course.title} — ${sectionLabel}`;
    const courseId = course.course_id || course.id || course_url;

    trackScreen(path, label, courseId);
    return () => flushCurrentScreen();
  }, [activeSection, course, course_url, user?.session]);

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold">Course not found</h2>
          <Link to="/dashboard" className="text-primary mt-2 block hover:underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const ActiveComponent = sectionComponents[activeSection];
  const activeNav = navItems.find(n => n.id === activeSection);

  return (
    <div className="flex h-full min-h-screen pt-24 bg-background relative">
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        className="fixed top-0 left-0 bottom-0 w-64 z-40 glass-dark border-r border-white/10 overflow-y-auto lg:hidden"
      >
        <SidebarContent course={course} navItems={navItems} activeSection={activeSection}
          onSelect={(id) => { setActiveSection(id); setSidebarOpen(false); }} />
      </motion.aside>

      <aside className="hidden lg:flex w-64 flex-col flex-shrink-0 glass border-r border-black/8 overflow-y-auto sticky top-0 h-screen">
        <SidebarContent course={course} navItems={navItems} activeSection={activeSection}
          onSelect={setActiveSection} />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-black/8 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-xl hover:bg-primary/10 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
          <span className="font-medium text-sm text-foreground truncate flex-1">{course.title}</span>
          {activeNav && (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
              <span className="text-sm text-primary font-semibold hidden sm:inline">{activeNav.label}</span>
            </>
          )}
        </div>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <ActiveComponent
                course={course}
                user={user}
                onCourseUpdate={(updates) => setCourse(prev => ({ ...prev, ...updates }))}
                onSelect={setActiveSection}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function SidebarContent({ course, navItems, activeSection, onSelect }) {
  return (
    <>
      <button
        onClick={() => onSelect('home')}
        className="w-full p-4 border-b border-white/10 bg-secondary/95 text-left hover:bg-secondary/80 transition-colors"
      >
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30 flex-shrink-0">
            <span className="font-heading font-bold text-sm text-primary-foreground">{course.title?.[0] || 'X'}</span>
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm text-sidebar-foreground leading-tight line-clamp-2">{course.title}</p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground mt-1 inline-block">PRO</span>
          </div>
        </div>
      </button>
      <nav className="flex-1 p-3 space-y-0.5">
        {navItems.map((item) => {
          const active = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left ${
                active
                  ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30'
                  : 'text-sidebar-foreground/70 hover:bg-white/8 hover:text-sidebar-foreground'
              }`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
              {active && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />}
            </button>
          );
        })}
      </nav>
    </>
  );
}